import React from "react";
import "./AntioxidantMeasurementTest.css";

const AntioxidantMeasurementTest = () => {
  return (
    <div className="antioxidant-test-page">

      {/* Full-Width Banner at the Top */}
      <section className="antioxidant-banner-section">
        <img
          src="/antioxidant-banner.png"
          alt="Antioxidant Measurement Test"
          className="antioxidant-banner-image"
        />
      </section>

      <div className="antioxidant-container">

        {/* Header */}
        <div className="antioxidant-hero">
          <span className="antioxidant-badge">
            DIGITAL HEALTHCARE
          </span>

          <h1>Antioxidant Measurement Test</h1>

          <p className="intro">
            It is a portable, non-invasive device that measures your body's
            antioxidant levels through a 15-second fingertip scan.
          </p>
        </div>

        {/* Key Benefits */}
        <section className="antioxidant-section">
          <h2>Key Benefits</h2>

          <div className="benefit-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🩸</div>
              <h3>No Blood Tests</h3>
              <p>
                It checks nutrient absorption instantly without needles or
                guesswork.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">📊</div>
              <h3>Tracks Wellness</h3>
              <p>
                It helps you see if your diet and supplements effectively
                combat oxidative stress over time.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">💡</div>
              <h3>Actionable Feedback</h3>
              <p>
                The color-coded score provides a baseline so you can adjust
                your lifestyle, sleep, and nutrition and re-scan to monitor
                progress.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="antioxidant-section">
          <h2>How It Works</h2>

          <div className="how-work-grid">
            <div className="work-card">
              <span className="work-number">01</span>
              <h3>Measures Carotenoids</h3>
              <p>
                The device assesses skin carotenoid levels—the natural
                antioxidant pigments from colorful fruits, vegetables, and
                supplements stored in your body tissues.
              </p>
            </div>

            <div className="work-card">
              <span className="work-number">02</span>
              <h3>Spectral Technology</h3>
              <p>
                It uses patent-pending Spectral Rai technology, taking
                hundreds of thousands of hyperspectral light absorption
                measurements per reading.
              </p>
            </div>

            <div className="work-card">
              <span className="work-number">03</span>
              <h3>AI Calibration</h3>
              <p>
                An AI-driven algorithm calibrated against Raman technology
                converts the light response into a personalized "Prysm Score".
              </p>
            </div>
          </div>
        </section>

        {/* Prysm Score Indicator */}
        <section className="antioxidant-section">
          <h2>Prysm Score Indicator Wellness Level</h2>

          <div className="score-table">
            <div className="score-row red-row">
              <span className="score-dot">🔴</span>
              <strong>100–190</strong>
              <span>RED</span>
              <span>Very Low</span>
            </div>

            <div className="score-row orange-row">
              <span className="score-dot">🟠</span>
              <strong>200–290</strong>
              <span>ORANGE</span>
              <span>Low</span>
            </div>

            <div className="score-row yellow-row">
              <span className="score-dot">🟡</span>
              <strong>300–490</strong>
              <span>YELLOW</span>
              <span>Moderate</span>
            </div>

            <div className="score-row green-row">
              <span className="score-dot">🟢</span>
              <strong>500–590</strong>
              <span>GREEN</span>
              <span>Good</span>
            </div>

            <div className="score-row blue-row">
              <span className="score-dot">🔵</span>
              <strong>600–1200+</strong>
              <span>BLUE</span>
              <span>Excellent</span>
            </div>
          </div>
        </section>

        {/* Easy to Understand */}
        <section className="antioxidant-section understand-section">
          <h2>Easy to Understand</h2>

          <div className="flow-box">
            <p>
              🔴 Very Low <span>→</span> 🟠 Low <span>→</span> 🟡 Moderate
              <span>→</span> 🟢 Good <span>→</span> 🔵 Excellent
            </p>

            <div className="process-flow">
              <span>Measure</span>
              <b>→</b>
              <span>Understand</span>
              <b>→</b>
              <span>Improve</span>
              <b>→</b>
              <span>Re-scan</span>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="disclaimer">
          <strong>Important Disclaimer</strong>
          <p>
            The Prysm Score is a non-invasive wellness indicator based on
            measured skin carotenoid levels. It is not a medical diagnosis or
            a direct measurement of total antioxidant capacity.
          </p>
        </section>

        {/* Detailed Score */}
        <section className="antioxidant-section">
          <h2>Prysm Score</h2>

          <div className="detailed-score-grid">
            <div className="detail-score-card red-card">
              <span>🔴 Red</span>
              <strong>100–190</strong>
              <small>Very Low</small>
              <p>Needs improvement</p>
            </div>

            <div className="detail-score-card orange-card">
              <span>🟠 Orange</span>
              <strong>Low</strong>
              <p>Improvement recommended</p>
            </div>

            <div className="detail-score-card yellow-card">
              <span>🟡 Yellow</span>
              <strong>200–290</strong>
              <small>Moderate</small>
              <p>Fair / needs attention</p>
            </div>

            <div className="detail-score-card green-card">
              <span>🟢 Green</span>
              <strong>300–490</strong>
              <small>Good</small>
              <p>Healthy antioxidant status</p>
            </div>

            <div className="detail-score-card blue-card">
              <span>🔵 Blue</span>
              <strong>500–590</strong>
              <small>Excellent</small>
              <p>Very high carotenoid level</p>
            </div>

            <div className="detail-score-card final-score-card">
              <strong>600–1200+</strong>
            </div>
          </div>
        </section>

        {/* Simple Display */}
        <section className="catalogue-section">
          <h2>Simple display for the catalogue</h2>

          <p className="color-flow">
            🔴 RED <span>→</span> 🟠 ORANGE <span>→</span> 🟡 YELLOW
            <span>→</span> 🟢 GREEN <span>→</span> 🔵 BLUE
          </p>

          <p className="level-flow">
            VERY LOW <span>→</span> LOW <span>→</span> MODERATE
            <span>→</span> GOOD <span>→</span> EXCELLENT
          </p>
        </section>

      </div>
    </div>
  );
};

export default AntioxidantMeasurementTest;
